import {ButtonGroup, ButtonProps, closeCurrentDialog} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {DmsDataGrid} from '../DmsDataGrid/DmsDataGrid';

interface SingleSelectGridPickerDialogProps extends TestIdProps {
  /** BE datagrid whose row id is the id to emit on confirm. */
  gridCode: string;
  /** Save-button label key; defaults to `general.actions.save`. */
  confirmLabel?: string;
  onConfirm: (id: string) => void;
  /** When set, renders a leading "clear" action (e.g. the optional responsible person). */
  onClear?: () => void;
}

/**
 * Single-select datagrid picker dialog: a full-height grid over a cancel/save footer (plus an
 * optional clear action). The grid row id is emitted directly on confirm. Backs the branch and
 * employee pickers, which differ only by grid code and the optional clear action.
 */
export function SingleSelectGridPickerDialog(props: SingleSelectGridPickerDialogProps) {
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleConfirm = () => {
    if (isNil(selectedRowId)) {
      return;
    }
    props.onConfirm(selectedRowId);
    closeCurrentDialog();
  };

  const handleClear = () => {
    props.onClear?.();
    closeCurrentDialog();
  };

  return (
    <VStack height="100%" spacing={4}>
      <Box flex={1} minHeight={0}>
        <DmsDataGrid
          gridCode={props.gridCode}
          onRowSelectionChange={(rows) => setSelectedRowId(head(rows)?.id ?? null)}
          data-testid={suffixTestId('dataGrid', props)}
        />
      </Box>
      <ButtonGroup
        align="right"
        buttons={buildArray<ButtonProps>()
          .when(isNotNil(props.onClear), {
            title: i18n.t('general.actions.clear'),
            onClick: handleClear,
            variant: 'ghostLink',
          })
          .add({
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
            variant: 'secondary',
          })
          .add({
            title: i18n.t(props.confirmLabel ?? 'general.actions.save'),
            onClick: handleConfirm,
            isDisabled: isNil(selectedRowId),
          })}
      />
    </VStack>
  );
}
